import {
  forwardRef,
  useState,
  useRef,
  useEffect,
  type ComponentProps,
  type ChangeEvent,
} from "react";
import { type VariantProps } from "class-variance-authority";
import { CircleAlert } from "lucide-react";

import { cn } from "@/lib/utils";
import type { inputVariants } from "@/components/ui/input";
import { Input } from "@/components/ui/input";

type LimitedInputProps = ComponentProps<"input"> &
  VariantProps<typeof inputVariants> & {
    maxLength: number;
    forbiddenPattern?: RegExp;
  };

const nativeInputValueSetter =
  typeof HTMLInputElement !== "undefined"
    ? Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")!.set!
    : undefined;

const LimitedInput = forwardRef<HTMLInputElement, LimitedInputProps>(
  (
    { className, maxLength, variant, forbiddenPattern, onChange, ...props },
    ref,
  ) => {
    const len = typeof props.value === "string" ? props.value.length : 0;
    const isUnderline = variant === "underline";

    const [flash, setFlash] = useState(false);
    const flashTimeout = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
      return () => {
        if (flashTimeout.current) clearTimeout(flashTimeout.current);
      };
    }, []);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (forbiddenPattern && onChange) {
        const raw = e.target.value;
        const sanitized = raw.replace(forbiddenPattern, "");

        if (sanitized !== raw) {
          if (flashTimeout.current) clearTimeout(flashTimeout.current);
          setFlash(true);
          flashTimeout.current = setTimeout(() => setFlash(false), 800);
        }

        nativeInputValueSetter?.call(e.target, sanitized);
        onChange(e);
      } else {
        onChange?.(e);
      }
    };

    return (
      <div className={cn("relative", className)}>
        <Input
          ref={ref}
          variant={variant}
          maxLength={maxLength}
          className="pr-16"
          onChange={handleChange}
          {...props}
        />
        <div
          className={cn(
            "pointer-events-none absolute top-1/2 flex -translate-y-1/2 items-center gap-1",
            isUnderline ? "right-0" : "right-3",
          )}
        >
          {flash && (
            <CircleAlert
              className="mb-0.5 h-4 w-4 stroke-[#B8001F]"
              aria-hidden="true"
            />
          )}
          <span className="font-trimMono text-[14px] tracking-[0.42px] text-[#48484a]">
            {len}/{maxLength}
          </span>
        </div>
      </div>
    );
  },
);
LimitedInput.displayName = "LimitedInput";

export { LimitedInput };
